import { Component, computed, signal, OnInit, inject } from '@angular/core';
import { InstructionsComponent, TableColumn } from '@r1-compass-library/components';
import { UserTableComponent } from './user-table/user-table.component';
import { IamDivision, IamRole, IamUser, IamUserSaveFormat, IamUserUpdateFormat } from '@r1-compass-library/models';
import {
    UserSettingsComponent,
    UserSettingsDialogData,
    UserSettingsDialogReturnData,
} from './user-table/user-settings/user-settings.component';
import { filterNil } from 'ngxtension/filter-nil';
import { MatDialog } from '@angular/material/dialog';
import { GlobalDivisionsStore } from '@r1-compass-library/store/divisions/global-divisions-store';
import { GlobalUsersStore } from '@r1-compass-library/store/users/globalUsersStore';
import { GlobalLoggedInUserStore } from '@r1-compass-library/store/logged-in-user/global-logged-in-user.store';

@Component({
    selector: 'r1-compass-app-admin-users',
    template: `
        <r1-compass-lib-instructions headerText="Manage User Permissions">
            To add a new user, click the '+Add User' button. To edit a user, find the user in the user list and choose
            'Edit' from the options menu.
        </r1-compass-lib-instructions>

        <r1-compass-app-user-table
            [columns]="userTableColumns"
            [userTableData]="filteredUsers()"
            [divisions]="iamDivisionStore.allIamDivisions()"
            [roles]="iamUserStore.roles()"
            [(userSearchState)]="userSearchState"
            (clickOption)="onClickOption($event)"
            (addUser)="onAddUser()" />
    `,
    imports: [InstructionsComponent, UserTableComponent],
})
export class AdminUsersComponent implements OnInit {
    readonly #dialog = inject(MatDialog);
    protected readonly iamDivisionStore = inject(GlobalDivisionsStore);
    protected readonly iamUserStore = inject(GlobalUsersStore);
    readonly #loggedInUserStore = inject(GlobalLoggedInUserStore);

    userTableColumns: TableColumn[] = [
        { columnDef: 'user', header: 'User', type: 'string' },
        { columnDef: 'divisions', header: 'Divisions', type: 'string' },
        { columnDef: 'role', header: 'Role', type: 'string' },
        {
            columnDef: 'edit',
            header: 'Edit',
            type: 'action',
        },
    ];

    private localUser = true; // TODO for SSO - this var is `true` for basic auth, needs to be dynamic later

    userSearchState = signal<string>('');

    filteredUsers = computed(() =>
        this.iamUserStore
            .users()
            .filter(user =>
                `${user.firstName} ${user.lastName}`?.toLowerCase().includes(this.userSearchState().toLowerCase())
            )
    );

    ngOnInit() {
        this.iamUserStore.getAllUsers();
        this.iamUserStore.getAllRoles();
    }

    onClickOption(option: { optionValue: string; id: number }) {
        if (option.optionValue === 'edit') {
            this.onUserEdit(option.id);
        }
    }

    onAddUser() {
        const divisions: IamDivision[] = this.iamDivisionStore.allIamDivisions();
        const roles: IamRole[] = this.iamUserStore.roles();
        const dialogRef = this.#dialog.open<UserSettingsComponent, UserSettingsDialogData>(UserSettingsComponent, {
            width: '80%',
            height: '70%',
            disableClose: true,
            data: {
                user: undefined,
                divisions: divisions,
                roles: roles,
                mode: 'add',
                currentDivisionId: this.#loggedInUserStore.selectedIamDivisionId()!,
            },
        });
        dialogRef
            .afterClosed()
            .pipe(filterNil())
            .subscribe((userData: UserSettingsDialogReturnData) => {
                const creatorAndUpdaterId = this.#loggedInUserStore.user()?.id;
                const newUser: IamUserSaveFormat = {
                    email: userData.email,
                    username: userData.username.toLowerCase(),
                    password: userData.password!,
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    createdBy: creatorAndUpdaterId!,
                    updatedBy: creatorAndUpdaterId!,
                    divisionSalesforceUserMap: userData.divisionSalesforceUserMap,
                    localUser: this.localUser,
                    enabled: true,
                    iamUserDivisionMaps: this.mapOutIamUserDivisionMaps(userData),
                };
                this.iamUserStore.createUser(newUser);
            });
    }

    onUserEdit(userId: number) {
        const user: IamUser = this.iamUserStore.users().find(user => user.id === userId)!;
        const divisions: IamDivision[] = this.iamDivisionStore.allIamDivisions();
        const roles: IamRole[] = this.iamUserStore.roles();
        const dialogRef = this.#dialog.open<UserSettingsComponent, UserSettingsDialogData>(UserSettingsComponent, {
            width: '80%',
            height: '70%',
            disableClose: true,
            data: {
                user: user,
                divisions: divisions,
                roles: roles,
                mode: 'edit',
                currentDivisionId: this.#loggedInUserStore.selectedIamDivisionId()!,
            },
        });
        dialogRef
            .afterClosed()
            .pipe(filterNil())
            .subscribe((userData: UserSettingsDialogReturnData) => {
                const updaterId = this.#loggedInUserStore.user()?.id;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { password, ...restOfUser } = user;
                let editedUser: IamUserUpdateFormat = structuredClone(restOfUser);
                const existingPasswordUnchanged = userData.password !== undefined;

                editedUser = {
                    ...editedUser,
                    email: userData.email,
                    username: userData.username.toLowerCase(),
                    ...(existingPasswordUnchanged && { password: userData.password }),
                    firstName: userData.firstName,
                    lastName: userData.lastName,
                    updatedBy: updaterId!,
                    divisionSalesforceUserMap: userData.divisionSalesforceUserMap,
                    localUser: this.localUser,
                    iamUserDivisionMaps: this.mapOutIamUserDivisionMaps(userData),
                };
                this.iamUserStore.updateUser(editedUser);
            });
    }

    private mapOutIamUserDivisionMaps(
        userData: UserSettingsDialogReturnData
    ): { divisionId: number; iamUserRoleDivMaps: { roleId: number }[] }[] {
        return userData.iamDivisions.map(div => ({
            divisionId: div,
            iamUserRoleDivMaps: [{ roleId: userData.iamRole }],
        }));
    }
}
